import React, { useContext } from 'react'
import { DateTime } from 'luxon'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import { checkEditionValid } from '@functions/publicationEdition'
import { itemRequiresArtwork } from '@functions/orderItem'
import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignQuoteItemActions from '@redux/modules/campaignQuoteItem'
import { validateCampaignQuotePrices as validatePricesAction } from '@redux/modules/campaignQuote'

import defaultFormState, { requiredFields } from '@models/campaignQuoteItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type {
  CampaignQuoteItemModel,
  CampaignQuoteItemRequestOptions,
  CreateCampaignQuoteItemPayload,
  CreateMultipleCampaignQuoteItemsPayload,
} from '@models/types'

const stringifyArrayKeys: (keyof CampaignQuoteItemModel)[] = ['options']
const stringifyObjectKeys: (keyof CampaignQuoteItemModel)[] = ['data']

export const generateUrls = (campaignQuoteItem?: Partial<CampaignQuoteItemModel>) => {
  const { id } = campaignQuoteItem || {}

  return {
    editCampaignQuoteItemDetailsUrl: `#/systemManager/campaignQuoteItems/${id}/edit/details`,
    editCampaignQuoteItemUrl: `#/systemManager/campaignQuoteItems/${id}/edit/`,
    campaignQuoteItemsIndexUrl: '#/systemManager/campaignQuoteItems',
  }
}

type BuildTitleStringParams = {
  displayTitle: string,
  publicationEditionValid: boolean,
  publicationWeek?: number,
}

const buildTitleString = (params: BuildTitleStringParams) => {
  const {
    displayTitle, publicationEditionValid, publicationWeek,
  } = params

  let title = displayTitle

  if (publicationWeek){
    title = `Week ${publicationWeek} | ${title}`
  }

  if (!publicationEditionValid){
    return title
  }

  return title
}

type CreateCampaignQuoteItemParams = {
  campaignQuoteItemParams: Partial<CampaignQuoteItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

const createCampaignQuoteItem = (params: CreateCampaignQuoteItemParams) => {
  const { dispatch, campaignQuoteItemParams, requestOptions } = params
  const { createCampaignQuoteItem: createFn } = campaignQuoteItemActions

  const updatedParams = mergeAndStringifyEntityObjects({}, campaignQuoteItemParams, stringifyObjectKeys, stringifyArrayKeys)

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions))
}

type CreateCampaignQuoteItemsParams = {
  dispatch: AppDispatch,
  itemPayload: CreateMultipleCampaignQuoteItemsPayload,
  orderType: 'bundle' | 'publication',
  requestOptions?: CampaignQuoteItemRequestOptions,
}

const createCampaignQuoteItems = (params: CreateCampaignQuoteItemsParams) => {
  const {
    dispatch, itemPayload, orderType, requestOptions,
  } = params
  const { createCampaignQuoteItems: createFn } = campaignQuoteItemActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, itemPayload, stringifyObjectKeys, stringifyArrayKeys)

  return dispatch(createFn(orderType, updatedParams, requestOptions))
}

type UpdateCampaignQuoteItemParams = {
  campaignQuoteItem: CampaignQuoteItemModel,
  campaignQuoteItemParams: Partial<CampaignQuoteItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

const updateCampaignQuoteItem = (params: UpdateCampaignQuoteItemParams) => {
  const {
    dispatch, campaignQuoteItem, campaignQuoteItemParams, requestOptions,
  } = params
  const { updateCampaignQuoteItem: updateFn } = campaignQuoteItemActions

  if (campaignQuoteItem?.campaign_quote_id){
    dispatch(validatePricesAction(campaignQuoteItem.campaign_quote_id))
  }

  const updatedParams = {
    id: campaignQuoteItem.id,
    ...campaignQuoteItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateCampaignQuoteItemOptionsParams = {
  campaignQuoteItem: CampaignQuoteItemModel,
  itemPayload: CreateCampaignQuoteItemPayload,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

const updateCampaignQuoteItemOptions = (params: UpdateCampaignQuoteItemOptionsParams) => {
  const {
    dispatch, campaignQuoteItem, itemPayload, requestOptions,
  } = params
  const { updateCampaignQuoteItemOptions: updateFn } = campaignQuoteItemActions

  const updatedParams = {
    ...itemPayload,
    options: typeof itemPayload.options === 'string' ? itemPayload.options : JSON.stringify(itemPayload.options),
  }

  return dispatch(updateFn(campaignQuoteItem.id, updatedParams, requestOptions))
}

type DuplicateCampaignQuoteItemParams = {
  campaignQuoteItem: CampaignQuoteItemModel,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

const duplicateCampaignQuoteItem = (params: DuplicateCampaignQuoteItemParams) => {
  const {
    dispatch, campaignQuoteItem, requestOptions,
  } = params
  const { duplicateCampaignQuoteItem: duplicateFn } = campaignQuoteItemActions

  return dispatch(duplicateFn(campaignQuoteItem, requestOptions))
}

type BulkAssignAttributesParams = {
  campaignQuoteItems: Partial<CampaignQuoteItemModel>[],
  dispatch: AppDispatch,
}

const bulkAssignAttributes = (params: BulkAssignAttributesParams) => {
  const { campaignQuoteItems, dispatch } = params
  const { bulkAssignAttributes: assignFn } = campaignQuoteItemActions

  return dispatch(assignFn(campaignQuoteItems))
}

type DeleteCampaignQuoteItemParams = {
  campaignQuoteItem: DeleteParams<CampaignQuoteItemModel>,
  dispatch: AppDispatch,
}

const deleteCampaignQuoteItem = (params: DeleteCampaignQuoteItemParams) => {
  const { dispatch, campaignQuoteItem } = params
  const { deleteCampaignQuoteItem: deleteFn } = campaignQuoteItemActions

  return dispatch(deleteFn(campaignQuoteItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteItemForm(
  campaignQuoteItem: Partial<CampaignQuoteItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteItemForm = useForm(
    defaultFormState,
    { entity: campaignQuoteItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuoteItem.id, campaignQuoteItem.cache_key],
  )

  return {
    ...campaignQuoteItemForm,
  }
}

export const useRelations = (campaignQuoteItem: Partial<CampaignQuoteItemModel> = {}) => {
  const {
    artwork_id,
    campaign_quote_id,
    campaign_quote_section_id,
    order_bundle_id,
    package_id,
    provider_id,
    provider_price_list_item_id,
    publication_edition_id,
    publication_id,
    service_id,
    service_product_id,
    user_id,
  } = campaignQuoteItem

  const {
    agencies,
    artworks,
    campaigns,
    campaignQuoteSections,
    campaignQuotes,
    orderBundles,
    providerPackages,
    providerPriceListItems,
    providers,
    publicationEditions,
    publications,
    serviceProducts,
    services,
    users,
  } = useSelector(reduxState => reduxState.entities)

  const artwork = artwork_id && artworks[artwork_id]
    ? artworks[artwork_id] : {}

  const campaignQuote = campaign_quote_id && campaignQuotes[campaign_quote_id]
    ? campaignQuotes[campaign_quote_id] : {}

  // @ts-expect-error TS2339
  const campaign = campaignQuote.campaign_id && campaigns[campaignQuote.campaign_id]
    // @ts-expect-error TS2339
    ? campaigns[campaignQuote.campaign_id] : {}

  // @ts-expect-error TS2339
  const agency = campaign.agency_id && agencies[campaign.agency_id]
    // @ts-expect-error TS2339
    ? agencies[campaign.agency_id] : {}

  const campaignQuoteSection = campaign_quote_section_id && campaignQuoteSections[campaign_quote_section_id]
    ? campaignQuoteSections[campaign_quote_section_id] : {}

  const orderBundle = order_bundle_id && orderBundles[order_bundle_id]
    ? orderBundles[order_bundle_id] : {}

  const providerPackage = package_id && providerPackages[package_id]
    ? providerPackages[package_id] : {}

  const provider = provider_id && providers[provider_id]
    ? providers[provider_id] : {}

  const providerPriceListItem = provider_price_list_item_id && providerPriceListItems[provider_price_list_item_id]
    ? providerPriceListItems[provider_price_list_item_id] : {}

  const publication = publication_id && publications[publication_id]
    ? publications[publication_id] : {}

  const publicationEdition = publication_edition_id && publicationEditions[publication_edition_id]
    ? publicationEditions[publication_edition_id] : {}

  const service = service_id && services[service_id]
    ? services[service_id] : {}

  const serviceProduct = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}

  const user = user_id && users[user_id]
    ? users[user_id] : {}

  return {
    agency,
    artwork,
    campaign,
    campaignQuote,
    campaignQuoteSection,
    orderBundle,
    provider,
    providerPackage,
    providerPriceListItem,
    publication,
    publicationEdition,
    service,
    serviceProduct,
    user,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

function useCampaignQuoteItem(
  initEntity: Partial<CampaignQuoteItemModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  const { permissions: { agency: agencyPermissions } } = useCurrentUser()

  // @ts-expect-error TS2322
  const { entity: campaignQuoteItem }: { entity: CampaignQuoteItemModel} = useLatestEntity(
    // @ts-expect-error TS2345
    initEntity,
    'campaignQuoteItems',
  )

  const {
    compulsory,
    display_title,
    id,
    item_type,
    publication_edition_id,
    publication_id,
    required_by,
    selected,
  } = campaignQuoteItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    agency,
    artwork,
    campaign,
    campaignQuote,
    campaignQuoteSection,
    publicationEdition,
    service,
    serviceProduct,
  } = useRelations(campaignQuoteItem)

  useReduxAction(
    'campaignQuoteItems',
    'loadCampaignQuoteItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuoteItems)

  const agencyPrice = digObject(campaignQuoteItem, 'data.agency_price')
  const publicationWeek = digObject(campaignQuoteItem, 'data.publication_week')
  const publicationEditionValid = checkEditionValid({
    publicationEditionId: publication_edition_id,
    // @ts-expect-error TS2740
    publicationEdition,
  })
  const recalculateEditions = digObject(agency, 'options.recalculate_quote_editions', false)
  const requiredByDateObject = required_by ? DateTime.fromFormat(required_by, 'yyyy-MM-dd') : undefined
  const requiredByDateString = requiredByDateObject ? requiredByDateObject?.toFormat('dd/MM/yyyy') : null
  const updatedTimestamp = digObject(campaignQuoteItem, 'dates.updated.integer', 0)

  const tags = digObject(campaignQuoteItem, 'data.tags', [])
  const hasTags = !!tags.length

  const isCompulsory = compulsory
  const isDeletable = !isCompulsory
  const isVisibilityToggleable = !isCompulsory
  const isSelected = isCompulsory || selected
  const isTemplatable = digObject(service, 'options.templatable', false)
  const isQuantityEditable = item_type !== 'custom' && !publication_edition_id

  const canCreateArtwork = isTemplatable && digObject(agencyPermissions, 'campaign.artwork.manage', false)

  const titleString = buildTitleString({
    // @ts-expect-error TS2322
    displayTitle: display_title,
    publicationWeek,
    publicationEditionValid,
  })

  const requiredItems = {
    artwork: itemRequiresArtwork({ service, serviceProduct }),
  }

  return {
    agencyPrice,
    artwork,
    callbacks: {
      bulkAssignAttributes: (campaignQuoteItems: Partial<CampaignQuoteItemModel>[]) => (
        bulkAssignAttributes({ campaignQuoteItems, dispatch })
      ),
      createCampaignQuoteItem: (
        campaignQuoteItemParams: Partial<CampaignQuoteItemModel>,
        entityOptions?: CampaignQuoteItemRequestOptions,
      ) => (
        createCampaignQuoteItem({ campaignQuoteItemParams, dispatch, requestOptions: entityOptions })
      ),
      createCampaignQuoteItems: (
        orderType: 'bundle' | 'publication',
        itemPayload: CreateMultipleCampaignQuoteItemsPayload,
        entityOptions?: CampaignQuoteItemRequestOptions,
      ) => (
        createCampaignQuoteItems({
          itemPayload, dispatch, orderType, requestOptions: entityOptions,
        })
      ),
      createOrEditCampaignQuoteItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteItemModal',
        payload: { campaignQuoteItem },
      }),
      deleteCampaignQuoteItem: () => deleteCampaignQuoteItem({ dispatch, campaignQuoteItem }),
      duplicateCampaignQuoteItem: (entityOptions?: CampaignQuoteItemRequestOptions) => (
        duplicateCampaignQuoteItem({ dispatch, campaignQuoteItem, requestOptions: entityOptions })
      ),
      editPublicationEdition: (
        params?: {
          callbacks?: {
            selectPublicationEditionId?: (payload: HandleCallbackActionParams) => void,
          },
          payload?: {
            currentEditionId?: number,
            publicationId?: number,
          },
        },
      ) => launchModal({
        callbacks,
        modalKey: 'SelectPublicationEditionModal',
        payload: {
          callbacks: params?.callbacks || {},
          currentEditionId: publication_edition_id,
          publicationId: publication_id,
          ...params?.payload,
        },
      }),
      openArtworkModal: (
        item?: CampaignQuoteItemModel,
      ) => launchModal({
        callbacks,
        modalKey: 'ArtworkBuilderSelectionModal',
        payload: {
          agency,
          campaign,
          campaignQuoteItem: item,
        },
      }),
      editQuantityAndOptions: (customOptions?: Record<string, string | (() => void)>) => launchModal({
        callbacks,
        modalKey: 'EditItemOptionsModal',
        payload: {
          callbacks: {
            updateItem: (
              itemPayload: CreateCampaignQuoteItemPayload,
              entityOptions?: CampaignQuoteItemRequestOptions,
            ) => updateCampaignQuoteItemOptions({
              campaignQuoteItem,
              dispatch,
              itemPayload,
              requestOptions: entityOptions,
            }),
          },
          entity: campaignQuoteItem,
          ...customOptions,
        },
      }),
      toggleCampaignQuoteItemSelection: (
        e: React.MouseEvent<HTMLElement>,
        entityOptions?: CampaignQuoteItemRequestOptions,
      ) => {
        e.preventDefault()
        e.stopPropagation()

        updateCampaignQuoteItem({
          campaignQuoteItem,
          campaignQuoteItemParams: { selected: !selected },
          dispatch,
          requestOptions: entityOptions,
        })
      },
      updateCampaignQuoteItem: (
        campaignQuoteItemParams: Partial<CampaignQuoteItemModel>,
        entityOptions?: CampaignQuoteItemRequestOptions,
      ) => (
        updateCampaignQuoteItem({
          campaignQuoteItem, campaignQuoteItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateCampaignQuoteItemOptions: (
        itemPayload: CreateCampaignQuoteItemPayload,
        entityOptions?: CampaignQuoteItemRequestOptions,
      ) => (
        updateCampaignQuoteItemOptions({
          campaignQuoteItem, dispatch, itemPayload, requestOptions: entityOptions,
        })
      ),
    },
    canCreateArtwork,
    campaign,
    campaignQuote,
    campaignQuoteItem,
    campaignQuoteSection,
    creating,
    deleting,
    hasTags,
    isCompulsory,
    isDeletable,
    isQuantityEditable,
    isSelected,
    isVisibilityToggleable,
    loading: loading && !loadedIds.includes(campaignQuoteItem.id),
    publication_id,
    publicationEdition,
    publicationEditionValid,
    publicationWeek,
    recalculateEditions,
    requiredByDateObject,
    requiredItems,
    tags,
    titleString,
    requiredByDateString,
    updatedTimestamp,
    updating,
    urls: generateUrls(campaignQuoteItem),
  }
}

export type UseCampaignQuoteItemPayload = ReturnType<typeof useCampaignQuoteItem>

export default useCampaignQuoteItem
